/**
 * Schedules a callback during idle periods, or falls back to setTimeout.
 * @type {(callback: IdleRequestCallback, options?: IdleRequestOptions) => number}
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback === 'function'
    ? (cb, opts) => window.requestIdleCallback(cb, opts)
    : (cb) => window.setTimeout(cb, 0);

/**
 * Returns a promise that resolves after yielding to the main thread.
 * @see https://web.dev/articles/optimize-long-tasks#scheduler-yield
 */
export const yieldToMainThread = () => {
  if ('yield' in scheduler) {
    // @ts-ignore - TypeScript doesn't recognize the yield method yet.
    return scheduler.yield();
  }

  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });
};

/**
 * Tells if we are on a low power device based on the number of CPU cores and RAM
 * @returns {boolean} True if we are on a low power device
 */
export function isLowPowerDevice() {
  return (
    Number(navigator.hardwareConcurrency) <= 2 ||
    Number(navigator.deviceMemory) <= 2
  );
}

/**
 * Check if the browser supports View Transitions API.
 * @returns {boolean}
 */
export function supportsViewTransitions() {
  return typeof document.startViewTransition === 'function';
}

/**
 * Detect in-app WebViews known to mishandle cross-document view transitions.
 *
 * @param {string} [userAgent=navigator.userAgent]
 * @returns {boolean}
 */
export function shouldDisableCrossDocumentViewTransitions(
  userAgent = navigator.userAgent
) {
  const ua = userAgent || '';

  const androidWebView =
    /\bAndroid\b/i.test(ua) &&
    /;\s?wv\)/i.test(ua);

  const knownInAppBrowser =
    /\b(FBAN|FBAV|FB_IAB|FBIOS|Instagram|musical_ly|Bytedance|BytedanceWebview|trill|TikTok)(?:\b|_)/i.test(
      ua
    );

  return androidWebView || knownInAppBrowser;
}

/**
 * Current view transition.
 * @type {{ current: Promise<void> | undefined }}
 */
export const viewTransition = {
  current: undefined,
};

/**
 * Functions to run when a view transition of a given type starts.
 *
 * @type {{ [key: string]: () => Promise<(() => void) | undefined> }}
 */
const viewTransitionTypes = {
  'product-grid': async () => {
    const grid = document.querySelector('.product-grid');

    const productCards = /** @type {HTMLElement[]} */ ([
      ...document.querySelectorAll(
        '.product-grid .product-grid__item'
      ),
    ]);

    if (!grid || !productCards.length) return;

    await new Promise((resolve) =>
      requestIdleCallback(() => {
        const cardsToAnimate = getCardsToAnimate(
          grid,
          productCards
        );

        productCards.forEach((card, index) => {
          if (index < cardsToAnimate) {
            card.style.setProperty(
              'view-transition-name',
              `product-card-${card.dataset.productId}`
            );
          } else {
            card.style.setProperty(
              'content-visibility',
              'hidden'
            );
          }
        });

        resolve(null);
      })
    );

    return () =>
      productCards.forEach((card) => {
        card.style.removeProperty('view-transition-name');
        card.style.removeProperty('content-visibility');
      });
  },
};

/**
 * Starts a view transition.
 *
 * @param {() => void} callback
 * @param {string[]} [types]
 * @returns {Promise<void>}
 */
export function startViewTransition(callback, types) {
  if (
    !supportsViewTransitions() ||
    isLowPowerDevice() ||
    prefersReducedMotion() ||
    shouldDisableCrossDocumentViewTransitions()
  ) {
    return Promise.resolve(callback());
  }

  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    const cleanupFunctions = [];

    if (types) {
      for (const type of types) {
        if (viewTransitionTypes[type]) {
          const cleanupFunction =
            await viewTransitionTypes[type]();

          if (cleanupFunction) {
            cleanupFunctions.push(cleanupFunction);
          }
        }
      }
    }

    const transition =
      document.startViewTransition(callback);

    if (!viewTransition.current) {
      viewTransition.current = transition.finished;
    }

    if (types) {
      types.forEach((type) => {
        transition.types.add(type);
      });
    }

    transition.finished.then(() => {
      viewTransition.current = undefined;

      cleanupFunctions.forEach((cleanupFunction) => {
        cleanupFunction();
      });

      resolve();
    });
  });
}

/**
 * @typedef {{ [key: string]: string | undefined }} Headers
 */

/**
 * @typedef {Object} FetchConfig
 * @property {string} method
 * @property {Headers} headers
 * @property {string | FormData | undefined} [body]
 */

/**
 * Creates a fetch configuration object.
 *
 * @param {string} [type='json']
 * @param {Object} [config]
 * @param {FetchConfig['body']} [config.body]
 * @param {FetchConfig['headers']} [config.headers]
 * @returns {RequestInit}
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Headers} */
  const headers = {
    'Content-Type': 'application/json',
    Accept: `application/${type}`,
    ...config.headers,
  };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers: /** @type {HeadersInit} */ (headers),
    body: config.body,
  };
}

/**
 * Creates a debounced function.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn
 * @param {number} wait
 * @returns {T & { cancel(): void }}
 */
export function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timeout;

  /** @param {...any} args */
  function debounced(...args) {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      fn.apply(this, args);
    }, wait);
  }

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return /** @type {T & { cancel(): void }} */ (
    debounced
  );
}

/**
 * Creates a throttled function.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn
 * @param {number} delay
 * @returns {T & { cancel(): void }}
 */
export function throttle(fn, delay) {
  let lastCall = 0;

  /** @param {...any} args */
  function throttled(...args) {
    const now = performance.now();

    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }

  throttled.cancel = () => {
    lastCall = performance.now();
  };

  return /** @type {T & { cancel(): void }} */ (
    throttled
  );
}

/**
 * Reduced-motion media query.
 * @type {MediaQueryList}
 */
const reducedMotion = matchMedia(
  '(prefers-reduced-motion: reduce)'
);

/**
 * @returns {boolean}
 */
export function prefersReducedMotion() {
  return reducedMotion.matches;
}

/**
 * Normalize a string.
 *
 * @param {string} str
 * @returns {string}
 */
export function normalizeString(str) {
  return str
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Calls a callback once the document is loaded.
 *
 * @param {() => void} callback
 */
export function onDocumentLoaded(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback);
  }
}

/**
 * Calls a callback when the DOM is ready.
 *
 * @param {() => void} callback
 * @param {AddEventListenerOptions} [options]
 */
export function onDocumentReady(callback, options) {
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      callback,
      options
    );
  } else {
    callback();
  }
}

/**
 * Removes will-change after animation completion.
 *
 * @param {AnimationEvent} event
 */
export function removeWillChangeOnAnimationEnd(event) {
  const target = event.target;

  if (target && target instanceof HTMLElement) {
    target.style.setProperty('will-change', 'unset');

    target.removeEventListener(
      'animationend',
      removeWillChangeOnAnimationEnd
    );
  }
}

/**
 * Waits for all document timeline animations to finish.
 *
 * @param {Element | Element[]} elements
 * @param {() => void} [callback]
 * @param {Object} [options]
 * @returns {Promise<void>}
 */
export function onAnimationEnd(
  elements,
  callback,
  options = { subtree: true }
) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) =>
        element.getAnimations(options)
      )
    : elements.getAnimations(options);

  const animationPromises = animations.reduce(
    (acc, animation) => {
      if (animation.timeline instanceof DocumentTimeline) {
        acc.push(animation.finished);
      }

      return acc;
    },
    /** @type {Promise<Animation>[]} */ ([])
  );

  return Promise.allSettled(animationPromises).then(
    callback
  );
}

/**
 * Elements that currently own the root scroll lock.
 *
 * @type {Set<Element>}
 */
const scrollLockOwners = new Set();

/**
 * Observer used to detect disconnected or externally closed owners.
 *
 * @type {MutationObserver | undefined}
 */
let scrollLockObserver;

/**
 * Removes inactive scroll-lock owners.
 *
 * Native dialogs can remain connected after being externally dismissed.
 * A connected but closed native dialog must not retain root scroll-lock.
 */
function pruneDisconnectedScrollLockOwners() {
  for (const owner of scrollLockOwners) {
    const isClosedDialog =
      owner instanceof HTMLDialogElement &&
      !owner.open;

    if (!owner.isConnected || isClosedDialog) {
      scrollLockOwners.delete(owner);
    }
  }
}

/**
 * Synchronizes the root scroll-lock attribute with active owners.
 */
function syncScrollLock() {
  pruneDisconnectedScrollLockOwners();

  if (scrollLockOwners.size > 0) {
    document.documentElement.setAttribute(
      'scroll-lock',
      ''
    );

    if (!scrollLockObserver) {
      scrollLockObserver = new MutationObserver(
        syncScrollLock
      );

      scrollLockObserver.observe(
        document.documentElement,
        {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['open'],
        }
      );
    }

    return;
  }

  document.documentElement.removeAttribute(
    'scroll-lock'
  );

  scrollLockObserver?.disconnect();
  scrollLockObserver = undefined;
}

/**
 * Locks root scrolling for an owning element.
 *
 * @param {Element} owner
 */
export function lockScroll(owner) {
  scrollLockOwners.add(owner);
  syncScrollLock();
}

/**
 * Unlocks root scrolling for an owning element.
 *
 * Calling this more than once for the same owner is safe.
 *
 * @param {Element} owner
 */
export function unlockScroll(owner) {
  scrollLockOwners.delete(owner);
  syncScrollLock();
}

/**
 * Checks whether a click occurred outside an element.
 *
 * @param {MouseEvent} event
 * @param {Element} element
 * @returns {boolean}
 */
export function isClickedOutside(event, element) {
  if (
    event.target instanceof HTMLDialogElement ||
    !(event.target instanceof Element)
  ) {
    return !isPointWithinElement(
      event.clientX,
      event.clientY,
      element
    );
  }

  return !element.contains(event.target);
}

/**
 * Checks whether a point is inside an element.
 *
 * @param {number} x
 * @param {number} y
 * @param {Element} element
 * @returns {boolean}
 */
export function isPointWithinElement(
  x,
  y,
  element
) {
  const { left, right, top, bottom } =
    element.getBoundingClientRect();

  return (
    x >= left &&
    x <= right &&
    y >= top &&
    y <= bottom
  );
}

/**
 * Large-screen media query.
 *
 * @type {MediaQueryList}
 */
export const mediaQueryLarge = matchMedia(
  '(min-width: 750px)'
);

/**
 * @returns {boolean}
 */
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

/**
 * @returns {boolean}
 */
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}

/**
 * @returns {boolean}
 */
export function isTouchDevice() {
  return (
    'ontouchstart' in window &&
    navigator.maxTouchPoints > 0
  );
}

/**
 * Clamps a number.
 *
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

/**
 * Calculates the center point of an element.
 *
 * @template {('x' | 'y')} T
 * @param {Element} element
 * @param {T} [axis]
 * @returns {T extends ('x' | 'y') ? number : {x: number, y: number}}
 */
export function center(element, axis) {
  const { left, width, top, height } =
    element.getBoundingClientRect();

  const point = {
    x: left + width / 2,
    y: top + height / 2,
  };

  if (axis) {
    return /** @type {any} */ (point[axis]);
  }

  return /** @type {any} */ (point);
}

/**
 * Calculates the start point of an element.
 *
 * @param {Element} element
 * @param {'x' | 'y'} [axis]
 * @returns {number | {x: number, y: number}}
 */
export function start(element, axis) {
  const { left, top } =
    element.getBoundingClientRect();

  const point = {
    x: left,
    y: top,
  };

  if (axis) {
    return /** @type {any} */ (point[axis]);
  }

  return /** @type {any} */ (point);
}

/**
 * Finds the closest numeric value.
 *
 * @param {number[]} values
 * @param {number} target
 * @returns {number}
 */
export function closest(values, target) {
  return values.reduce((previous, current) =>
    Math.abs(current - target) <
    Math.abs(previous - target)
      ? current
      : previous
  );
}

/**
 * Prevents an event's default action.
 *
 * @param {Event} event
 */
export function preventDefault(event) {
  event.preventDefault();
}

/**
 * Returns elements visible within a root.
 *
 * @template {Element} T
 * @param {Element} root
 * @param {T[] | undefined} elements
 * @param {number} [ratio=1]
 * @param {'x' | 'y'} [axis]
 * @returns {T[]}
 */
export function getVisibleElements(
  root,
  elements,
  ratio = 1,
  axis
) {
  if (!elements?.length) return [];

  const rootRect = root.getBoundingClientRect();

  return elements.filter((element) => {
    const {
      width,
      height,
      top,
      right,
      left,
      bottom,
    } = element.getBoundingClientRect();

    if (ratio < 1) {
      const intersectionLeft = Math.max(
        rootRect.left,
        left
      );

      const intersectionRight = Math.min(
        rootRect.right,
        right
      );

      const intersectionWidth = Math.max(
        0,
        intersectionRight - intersectionLeft
      );

      if (axis === 'x') {
        return (
          width > 0 &&
          intersectionWidth / width >= ratio
        );
      }

      const intersectionTop = Math.max(
        rootRect.top,
        top
      );

      const intersectionBottom = Math.min(
        rootRect.bottom,
        bottom
      );

      const intersectionHeight = Math.max(
        0,
        intersectionBottom - intersectionTop
      );

      if (axis === 'y') {
        return (
          height > 0 &&
          intersectionHeight / height >= ratio
        );
      }

      const intersectionArea =
        intersectionWidth * intersectionHeight;

      const elementArea = width * height;

      return (
        elementArea > 0 &&
        intersectionArea / elementArea >= ratio
      );
    }

    const isWithinX =
      left >= rootRect.left &&
      right <= rootRect.right;

    if (axis === 'x') {
      return isWithinX;
    }

    const isWithinY =
      top >= rootRect.top &&
      bottom <= rootRect.bottom;

    if (axis === 'y') {
      return isWithinY;
    }

    return isWithinX && isWithinY;
  });
}

/**
 * Returns the detected iOS version.
 *
 * @returns {{fullString: string, major: number, minor: number} | null}
 */
export function getIOSVersion() {
  const { userAgent } = navigator;
  const isIOS = /(iPhone|iPad)/i.test(userAgent);

  if (!isIOS) return null;

  const version =
    userAgent.match(/OS ([\d_]+)/)?.[1];

  const [major, minor] =
    version?.split('_') || [];

  if (!version || !major) return null;

  return {
    fullString: version.replace('_', '.'),
    major: parseInt(major, 10),
    minor: minor ? parseInt(minor, 10) : 0,
  };
}

/**
 * Determines which grid items should be animated.
 *
 * @param {Element} grid
 * @param {Element[]} cards
 * @returns {number}
 */
function getCardsToAnimate(grid, cards) {
  if (!grid || !cards || cards.length === 0) {
    return 0;
  }

  const itemSample = cards[0];

  if (!itemSample) return 0;

  const gridRect = grid.getBoundingClientRect();

  const visibleArea = {
    top: Math.max(0, gridRect.top),
    bottom: Math.min(
      window.innerHeight,
      gridRect.bottom
    ),
  };

  const visibleHeight = Math.round(
    visibleArea.bottom - visibleArea.top
  );

  if (visibleHeight <= 0) return 0;

  /** @type {import('product-card').ProductCard | null} */
  const cardSample =
    itemSample.querySelector('product-card');

  const gridStyle = getComputedStyle(grid);

  const galleryAspectRatio =
    cardSample?.refs?.cardGallery?.style.getPropertyValue(
      '--gallery-aspect-ratio'
    ) || '';

  let aspectRatio =
    parseFloat(galleryAspectRatio) || 0.5;

  if (galleryAspectRatio.includes('/')) {
    const [width = '1', height = '2'] =
      galleryAspectRatio.split('/');

    aspectRatio =
      parseInt(width, 10) /
      parseInt(height, 10);
  }

  const cardGap =
    parseInt(
      cardSample?.refs?.productCardLink?.style.getPropertyValue(
        '--product-card-gap'
      ) || ''
    ) || 12;

  const gridGap =
    parseInt(
      gridStyle.getPropertyValue(
        '--product-grid-gap'
      )
    ) || 12;

  const detailsSize =
    ((parseInt(gridStyle.fontSize) || 16) + 2) *
    2;

  const isMobile = window.innerWidth < 750;

  const cardWidth = isMobile
    ? Math.round(
        (gridRect.width - gridGap) / 2
      )
    : 100;

  const cardHeight =
    Math.round(cardWidth / aspectRatio) +
    cardGap +
    detailsSize;

  const columnsInGrid = isMobile
    ? 2
    : Math.floor(
        (gridRect.width + gridGap) /
          (cardWidth + gridGap)
      );

  const rowsInGrid = Math.ceil(
    (visibleHeight - gridGap) /
      (cardHeight + gridGap)
  );

  return columnsInGrid * rowsInGrid;
}

/**
 * Preloads an image.
 *
 * @param {string} src
 */
export function preloadImage(src) {
  const image = new Image();
  image.src = src;
}

export class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute('shimmer', '');
  }
}

if (!customElements.get('text-component')) {
  customElements.define(
    'text-component',
    TextComponent
  );
}

/**
 * Resets shimmer attributes.
 *
 * @param {Element} [container=document.body]
 */
export function resetShimmer(
  container = document.body
) {
  const shimmer =
    container.querySelectorAll('[shimmer]');

  shimmer.forEach((item) => {
    item.removeAttribute('shimmer');
  });
}

/**
 * Changes the browser theme color.
 *
 * @param {string} color
 */
export function changeMetaThemeColor(color) {
  const metaThemeColor =
    document.head.querySelector(
      'meta[name="theme-color"]'
    );

  if (metaThemeColor && color) {
    metaThemeColor.setAttribute(
      'content',
      color
    );
  }
}

/**
 * Returns the view URL parameter.
 *
 * @returns {string | null}
 */
export function getViewParameterValue() {
  return new URLSearchParams(
    window.location.search
  ).get('view');
}

/**
 * Parses an integer with a default fallback.
 *
 * @template {number|null} T
 * @param {string|number|null|undefined} value
 * @param {T} defaultValue
 * @returns {number|T}
 */
export function parseIntOrDefault(
  value,
  defaultValue
) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return defaultValue;
  }

  const parsed = parseInt(value.toString());

  return isNaN(parsed)
    ? defaultValue
    : parsed;
}

class Scheduler {
  /** @type {Set<() => void>} */
  #queue = new Set();

  /** @type {boolean} */
  #scheduled = false;

  /**
   * @param {() => void} task
   */
  schedule = async (task) => {
    this.#queue.add(task);

    if (!this.#scheduled) {
      this.#scheduled = true;

      if (viewTransition.current) {
        await viewTransition.current;
      }

      requestAnimationFrame(this.flush);
    }
  };

  flush = () => {
    for (const task of this.#queue) {
      setTimeout(task, 0);
    }

    this.#queue.clear();
    this.#scheduled = false;
  };
}

export const scheduler = new Scheduler();

/**
 * Executes a callback once per Shopify editor session.
 *
 * @param {HTMLElement} element
 * @param {string} sessionKeyName
 * @param {() => void} callback
 */
export function oncePerEditorSession(
  element,
  sessionKeyName,
  callback
) {
  const isInThemeEditor =
    window.Shopify?.designMode;

  const shopifyEditorSectionId = JSON.parse(
    element.dataset.shopifyEditorSection || '{}'
  ).id;

  const shopifyEditorBlockId = JSON.parse(
    element.dataset.shopifyEditorBlock || '{}'
  ).id;

  const editorId =
    shopifyEditorSectionId ||
    shopifyEditorBlockId;

  const uniqueSessionKey =
    `${sessionKeyName}-${editorId}`;

  if (
    isInThemeEditor &&
    sessionStorage.getItem(uniqueSessionKey)
  ) {
    return;
  }

  callback();

  if (isInThemeEditor) {
    sessionStorage.setItem(
      uniqueSessionKey,
      'true'
    );
  }
}

/**
 * ResizeObserver that skips its initial callback.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) {
        return callback(entries, this);
      }

      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Sets the header menu style.
 */
export function setHeaderMenuStyle() {
  const headerComponent =
    /** @type {HTMLElement | null} */ (
      document.querySelector(
        '#header-component'
      )
    );

  if (headerComponent) {
    window.requestAnimationFrame(() => {
      const overflowList =
        headerComponent.querySelector(
          'overflow-list'
        );

      const hasReachedMinimum =
        overflowList &&
        overflowList.hasAttribute(
          'minimum-reached'
        );

      headerComponent.dataset.menuStyle =
        isTouchDevice() ||
        hasReachedMinimum
          ? 'drawer'
          : 'menu';
    });
  }
}

/**
 * Calculates header-group height.
 *
 * @param {HTMLElement | null} [header]
 * @param {HTMLElement | null} [headerGroup]
 * @returns {number}
 */
export function calculateHeaderGroupHeight(
  header = document.querySelector(
    '#header-component'
  ),
  headerGroup = document.querySelector(
    '#header-group'
  )
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;

  for (let index = 0; index < children.length; index++) {
    const element = children[index];

    if (
      element === header ||
      !(element instanceof HTMLElement)
    ) {
      continue;
    }

    totalHeight += element.offsetHeight;
  }

  if (
    header instanceof HTMLElement &&
    header.hasAttribute('transparent') &&
    header.parentElement?.nextElementSibling
  ) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

/**
 * Updates the transparent-header offset.
 */
function updateTransparentHeaderOffset() {
  const header =
    document.querySelector(
      '#header-component'
    );

  const headerGroup =
    document.querySelector('#header-group');

  const hasHeaderSection =
    headerGroup?.querySelector(
      '.header-section'
    );

  if (
    !hasHeaderSection ||
    !header?.hasAttribute('transparent')
  ) {
    document.body.style.setProperty(
      '--transparent-header-offset-boolean',
      '0'
    );

    return;
  }

  const hasImmediateSection =
    hasHeaderSection.nextElementSibling
      ?.classList.contains('shopify-section');

  const shouldApplyOffset =
    !hasImmediateSection ? '1' : '0';

  document.body.style.setProperty(
    '--transparent-header-offset-boolean',
    shouldApplyOffset
  );
}

/**
 * Updates header height properties.
 */
function updateHeaderHeights() {
  const header =
    document.querySelector(
      'header-component'
    );

  if (!(header instanceof HTMLElement)) {
    return;
  }

  const headerHeight = header.offsetHeight;

  const headerGroupHeight =
    calculateHeaderGroupHeight(header);

  const headerTopRow =
    /** @type {HTMLElement | null} */ (
      header.querySelector(
        '.header__row--top'
      )
    );

  document.body.style.setProperty(
    '--header-height',
    `${headerHeight}px`
  );

  document.body.style.setProperty(
    '--header-group-height',
    `${headerGroupHeight}px`
  );

  if (headerTopRow) {
    window.requestAnimationFrame(() => {
      header.style.setProperty(
        '--top-row-height',
        `${headerTopRow.offsetHeight}px`
      );
    });
  }
}

/**
 * Updates all header custom properties.
 */
export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
  setHeaderMenuStyle();
}

// Theme is not defined in some layouts, such as the gift-card page.
if (typeof Theme !== 'undefined') {
  Theme.utilities = {
    ...Theme.utilities,
    scheduler,
  };
}